import {
  FC,
  useState,
  useRef,
  useCallback,
  FormEventHandler,
  useEffect,
} from 'react'
import type { CountryCode } from 'libphonenumber-js'
import { getCountries } from 'react-phone-number-input'
import { t } from 'i18next'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { Icon } from '../Icon'
import { Input } from '../Input/Input'
import { ActionSheet } from '../ActionSheet'
import { Popover } from '../Popover'
import { useMediaQuery } from '../../hooks'
import { Text } from '../Text'

import type { CountryOption, InputPhoneNumberVariant } from './types'
import { formatCountryCallingCode } from './utils'
import { Flag } from './Flag'

interface CountrySelectorProps {
  onChange: (value: CountryCode) => void
  selectedCountryOption: CountryOption
  setSelectedCountryOption: (value: CountryOption) => void
  variant?: InputPhoneNumberVariant
}

const countryOptions = getCountries()
  .map((key) => key as CountryCode)
  .reduce<CountryOption[]>((result, flagKey) => {
    const countryCallingCode = formatCountryCallingCode(flagKey)

    return [
      ...result,
      {
        key: flagKey,
        code: countryCallingCode,
        name: `countries.${flagKey}`,
      },
    ]
  }, [])

interface ContentProps {
  search: string
  handleChange: FormEventHandler<HTMLInputElement>
  options: CountryOption[]
  onChange?: (value: CountryCode) => void
  setSelectedCountryOption: (value: CountryOption) => void
  variant?: InputPhoneNumberVariant
}

const Content: FC<ContentProps> = ({
  search,
  handleChange,
  options,
  onChange,
  setSelectedCountryOption,
  variant,
}) => {
  const { t } = useTranslation()
  return (
    <div className="overflow-visible">
      <div className="sticky pt-4 mb-4 -mt-4 -inset-4 bg-white z-10">
        <Input
          className="w-full [&_.input-group__control]:border-primary-default dark:[&_.input-group__control]:focus-within:!border-[black] dark:[&_input]:!text-black"
          placeholder={`${t('phone_input.search_country')}...`}
          id="search-country-code"
          aria-label="Search country"
          name="search"
          value={search}
          onChange={handleChange}
          startAdornment={
            <Icon.Search className="text-black" width={16} height={16} />
          }
          variant={variant}
        />
      </div>
      <div className="scrollbar scrollbar--simple grid gap-2 max-h-[159px] mr-2 overflow-x-hidden sm:max-h-[132[x]] sm:gap-3 sm:overflow-y-scroll">
        {options.map((option) => {
          const { key, code } = option
          return (
            <button
              key={`${key}/${code}`}
              type="button"
              className="flex items-center h-12 pl-3 mr-3 hover:bg-surface-secondary-default transition-colors duration-200 ease-out sm:h-10 sm:px-4 min-w-full w-[max-content]"
              onClick={() => {
                onChange?.(key)
                setSelectedCountryOption(option)
              }}
            >
              <Flag code={key} />

              <span className="ml-3 text-md tracking-[0.56px] text-default sm:text-sm sm:tracking-[0.28px] sm:font-regular">
                {t(`countries.${key}`)}
              </span>
              <span className="ml-1 text-sm tracking-[0.28px] text-action-primary-hovered">
                {code}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
interface TriggerProps {
  selectedCountryOption: CountryOption
  variant?: InputPhoneNumberVariant
}

const Trigger = ({
  selectedCountryOption,
  variant,
  ...props
}: TriggerProps) => (
  <button
    type="button"
    className={classNames(
      'flex justify-center items-center gap-1',
      variant === 'underlined' ? 'w-7 h-4' : 'w-12 h-12',
    )}
    {...props}
  >
    <Flag code={selectedCountryOption.key} isSelected />
    <span>
      <Icon.ChevronDown
        className="text-black dark:text-white"
        width={8}
        height={8}
      />
    </span>
  </button>
)

export const CountrySelect: FC<CountrySelectorProps> = ({
  onChange,
  selectedCountryOption,
  setSelectedCountryOption,
  variant,
}) => {
  const isDesktop = useMediaQuery(({ screens }) => `(min-width: ${screens.sm})`)
  const [search, setSearch] = useState<string>('')
  const [filteredOptions, setFilteredOptions] =
    useState<CountryOption[]>(countryOptions)

  const container = useRef<HTMLDivElement | null>(null)

  const handleChange: FormEventHandler<HTMLInputElement> = useCallback(
    (evt) => setSearch(evt.currentTarget.value),
    [],
  )

  useEffect(() => {
    if (search)
      setFilteredOptions(
        countryOptions.filter(
          ({ key, code }) =>
            t(`countries.${key}`)
              .toLowerCase()
              .includes(search.toLowerCase()) || code.includes(search),
        ),
      )
    else {
      setFilteredOptions(countryOptions)
    }
  }, [search])

  return (
    <div
      ref={container}
      className={classNames(
        'relative m-0 gap-1',
        variant == 'default' &&
          'flex justify-center align-middle border-r border-primary-default dark:border-white dark:group-focus-within:!border-white dark:group-hover:border-primary-hovered',
      )}
    >
      {!isDesktop ? (
        <ActionSheet>
          {({ isOpen, onOpen }) => (
            <>
              <ActionSheet.Trigger>
                <Trigger
                  selectedCountryOption={selectedCountryOption}
                  variant={variant}
                />
              </ActionSheet.Trigger>

              <ActionSheet.Content className="bg-white rounded-t-[30px] z-10 shadow-2 overflow-hidden absolute sm:top-[58px] min-w-[342px] translate-y-0">
                <ActionSheet.Header className="bg-white">
                  <Text variant="body-md" className="font-regular">
                    {t('phone_input.select_your_country')}
                  </Text>
                </ActionSheet.Header>
                <ActionSheet.Body className="bg-white">
                  <Content
                    {...{
                      search,
                      handleChange,
                      options: filteredOptions,
                      onChange,
                      setSelectedCountryOption,
                    }}
                  />
                </ActionSheet.Body>
              </ActionSheet.Content>
            </>
          )}
        </ActionSheet>
      ) : (
        <Popover scrollLock={false}>
          {({ isOpen, onClose }) => (
            <>
              <Popover.Trigger>
                <Trigger
                  selectedCountryOption={selectedCountryOption}
                  variant={variant}
                />
              </Popover.Trigger>
              {isOpen && (
                <Popover.Content
                  onClickAway={onClose}
                  className="bg-white z-10 shadow-2 overflow-hidden absolute top-[58px] min-w-[342px] translate-y-0 left-0 p-4"
                >
                  <Content
                    {...{
                      search,
                      handleChange,
                      options: filteredOptions,
                      onChange,
                      setSelectedCountryOption,
                    }}
                  />
                </Popover.Content>
              )}
            </>
          )}
        </Popover>
      )}
    </div>
  )
}
